import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { PageHead, Skel } from '../components/ui'
import { thaiDateTime } from '../lib/format'

type Mode = 'allow' | 'deny'

interface AccessFence {
  id: string
  cidr: string
  country_codes: string[]
  mode: Mode
  note: string
  enabled: boolean
  created_at: string
}

interface FenceDecision {
  allowed: boolean
  reason: string
  ip: string
  country: string
}

const FENCES_KEY = ['access-fences']

const modeLabel: Record<Mode, string> = {
  allow: 'อนุญาต',
  deny: 'ปฏิเสธ',
}

const modeHint: Record<Mode, string> = {
  allow: 'เมื่อมีกฎอนุญาตอย่างน้อยหนึ่งข้อ ระบบจะปฏิเสธทุกคำขอที่ไม่ตรงกับกฎอนุญาต',
  deny: 'กฎปฏิเสธมีผลเหนือกฎอนุญาตเสมอ และใช้ร่วมกับกฎอนุญาตได้',
}

export function AccessFence() {
  const qc = useQueryClient()

  const [cidr, setCidr] = useState('')
  const [countries, setCountries] = useState('')
  const [mode, setMode] = useState<Mode>('allow')
  const [note, setNote] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [confirmId, setConfirmId] = useState<string | null>(null)

  const [checkIp, setCheckIp] = useState('')
  const [checkCountry, setCheckCountry] = useState('')
  const [checked, setChecked] = useState<FenceDecision | null>(null)

  const fences = useQuery({
    queryKey: FENCES_KEY,
    queryFn: () => list<AccessFence>('/v1/access-fences'),
    staleTime: 15_000,
  })

  const create = useMutation({
    mutationFn: () => apiWrite<AccessFence>('/v1/access-fences', 'POST', {
      cidr: cidr.trim(),
      country_codes: countries.split(',').map((code) => code.trim().toUpperCase()).filter(Boolean),
      mode,
      note: note.trim(),
      enabled,
    }),
    onSuccess: () => {
      setCidr('')
      setCountries('')
      setNote('')
      setEnabled(true)
      void qc.invalidateQueries({ queryKey: FENCES_KEY })
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => apiWrite<{ id: string; deleted: boolean }>(`/v1/access-fences/${id}`, 'DELETE'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: FENCES_KEY }),
    onSettled: () => setConfirmId(null),
  })

  const check = useMutation({
    mutationFn: () => api<FenceDecision>('/v1/access-fences/check', {
      method: 'POST',
      body: JSON.stringify({ ip: checkIp.trim(), country: checkCountry.trim() }),
    }),
    onSuccess: (result) => setChecked(result),
    onError: () => setChecked(null),
  })

  const rows = fences.data ?? []
  const enabledCount = rows.filter((row) => row.enabled).length
  const denyCount = rows.filter((row) => row.enabled && row.mode === 'deny').length
  const canSubmit = Boolean(cidr.trim() || countries.trim()) && !create.isPending

  return (
    <>
      <PageHead
        title="ควบคุมการเข้าถึงตาม IP และประเทศ"
        sub="กำหนดว่าผู้ใช้ขององค์กรเข้าถึงระบบได้จากเครือข่ายหรือประเทศใด · กฎปฏิเสธมีผลเหนือกฎอนุญาตเสมอ"
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{rows.length} กฎทั้งหมด</span>
        <span className="chip">{enabledCount} กฎที่เปิดใช้</span>
        <span className="chip">{denyCount} กฎปฏิเสธ</span>
        <span className="chip">ค่าเริ่มต้นเมื่อยังไม่มีกฎ: อนุญาตทั้งหมด</span>
      </div>

      <div className="grid-main">
        <div className="card card-pad">
          <div className="card-label">เพิ่มกฎใหม่</div>

          <div className="field">
            <label htmlFor="fence-cidr">ช่วง IP หรือ CIDR</label>
            <input
              id="fence-cidr"
              className="input mono"
              placeholder="203.0.113.0/24, 2001:db8::/32"
              value={cidr}
              onChange={(e) => setCidr(e.target.value)}
            />
            <div className="dim" style={{ fontSize: 10.5, marginTop: 6, lineHeight: 1.7 }}>
              รองรับ IPv4 และ IPv6 · คั่นหลายช่วงด้วยจุลภาค · เว้นว่างได้หากระบุประเทศ
            </div>
          </div>

          <div className="field">
            <label htmlFor="fence-countries">รหัสประเทศ</label>
            <input
              id="fence-countries"
              className="input mono"
              placeholder="TH, SG"
              value={countries}
              onChange={(e) => setCountries(e.target.value)}
            />
            <div className="dim" style={{ fontSize: 10.5, marginTop: 6, lineHeight: 1.7 }}>
              รหัส ISO 3166-1 สองตัวอักษร คั่นด้วยจุลภาค · ใช้ค่าจาก header CF-IPCountry ของ Cloudflare
            </div>
          </div>

          <div className="field">
            <label htmlFor="fence-mode">การทำงานเมื่อตรงกฎ</label>
            <select
              id="fence-mode"
              className="select"
              value={mode}
              onChange={(e) => setMode(e.target.value as Mode)}
            >
              <option value="allow">อนุญาต</option>
              <option value="deny">ปฏิเสธ</option>
            </select>
            <div className="dim" style={{ fontSize: 10.5, marginTop: 6, lineHeight: 1.7 }}>
              {modeHint[mode]}
            </div>
          </div>

          <div className="field">
            <label htmlFor="fence-note">หมายเหตุ</label>
            <input
              id="fence-note"
              className="input"
              maxLength={500}
              placeholder="เช่น เครือข่ายสำนักงานใหญ่"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <label className="row" style={{ gap: 8, marginTop: 14, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              style={{ accentColor: 'var(--color-gold)' }}
            />
            <span className="dim" style={{ fontSize: 12 }}>เปิดใช้กฎนี้ทันที</span>
          </label>

          {create.isError ? (
            <div className="form-error" style={{ marginTop: 12 }}>
              {create.error instanceof Error ? create.error.message : 'บันทึกกฎไม่สำเร็จ'}
            </div>
          ) : null}

          <button
            className="btn btn-primary"
            style={{ marginTop: 18 }}
            disabled={!canSubmit}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'กำลังบันทึก…' : 'เพิ่มกฎ'}
          </button>
        </div>

        <div className="card card-pad">
          <div className="card-label">ทดสอบการเข้าถึง</div>
          <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.8, marginTop: 8 }}>
            ตรวจสอบว่าที่อยู่และประเทศที่ระบุจะผ่านกฎปัจจุบันหรือไม่ · การทดสอบไม่บันทึกข้อมูลและไม่เปลี่ยนสิทธิ์
          </p>

          <div className="field" style={{ marginTop: 12 }}>
            <label htmlFor="fence-check-ip">ที่อยู่ IP</label>
            <input
              id="fence-check-ip"
              className="input mono"
              placeholder="203.0.113.10"
              value={checkIp}
              onChange={(e) => setCheckIp(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="fence-check-country">รหัสประเทศ</label>
            <input
              id="fence-check-country"
              className="input mono"
              placeholder="TH"
              maxLength={2}
              value={checkCountry}
              onChange={(e) => setCheckCountry(e.target.value.toUpperCase())}
            />
            <div className="dim" style={{ fontSize: 10.5, marginTop: 6 }}>
              เว้นว่างเพื่อใช้ค่าจากคำขอปัจจุบัน
            </div>
          </div>

          <button
            className="btn btn-ghost"
            style={{ marginTop: 14 }}
            disabled={check.isPending}
            onClick={() => check.mutate()}
          >
            {check.isPending ? 'กำลังตรวจสอบ…' : 'ตรวจสอบ'}
          </button>

          {check.isError ? (
            <div className="form-error" style={{ marginTop: 12 }}>
              {check.error instanceof Error ? check.error.message : 'ตรวจสอบไม่สำเร็จ'}
            </div>
          ) : null}

          {checked ? (
            <div
              style={{
                marginTop: 16,
                padding: '12px 14px',
                border: '1px solid var(--color-hairline, rgba(255,255,255,0.08))',
                borderRadius: 8,
              }}
            >
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <span className={`chip${checked.allowed ? ' gold' : ''}`}
                  style={checked.allowed ? undefined : { color: 'var(--color-danger, #c97a7a)' }}>
                  {checked.allowed ? 'อนุญาต' : 'ปฏิเสธ'}
                </span>
                <span className="mono dim" style={{ fontSize: 11 }}>
                  {checked.ip || 'ไม่ทราบ IP'}{checked.country ? ` · ${checked.country}` : ''}
                </span>
              </div>
              <div className="dim" style={{ fontSize: 12, lineHeight: 1.8, marginTop: 10 }}>
                {checked.reason}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ marginTop: 22 }}>
        <div className="row" style={{ marginBottom: 10 }}>
          <span className="card-label">กฎทั้งหมด</span>
          <span className="chip" style={{ marginLeft: 'auto' }}>{rows.length} รายการ</span>
        </div>

        {fences.isLoading ? <Skel h={150} /> : fences.isError ? (
          <div className="card card-pad">
            <div className="form-error">โหลดกฎไม่สำเร็จ — ลองรีเฟรชหน้าอีกครั้ง</div>
          </div>
        ) : rows.length === 0 ? (
          <div className="card card-pad dim" style={{ fontSize: 12.5, lineHeight: 1.8 }}>
            ยังไม่มีกฎควบคุมการเข้าถึง · ระบบอนุญาตทุกเครือข่ายจนกว่าจะเพิ่มกฎอนุญาตหรือกฎปฏิเสธข้อแรก
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>ช่วงเครือข่าย</th>
                  <th>ประเทศ</th>
                  <th style={{ width: 110 }}>โหมด</th>
                  <th>หมายเหตุ</th>
                  <th style={{ width: 120 }}>สร้างเมื่อ</th>
                  <th style={{ width: 90 }}>สถานะ</th>
                  <th style={{ width: 150 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} style={{ cursor: 'default' }}>
                    <td className="mono" style={{ fontSize: 11.5 }}>{row.cidr || 'ทุกเครือข่าย'}</td>
                    <td>
                      {row.country_codes.length === 0 ? (
                        <span className="dim">ทุกประเทศ</span>
                      ) : (
                        <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                          {row.country_codes.map((code) => <span key={code} className="chip">{code}</span>)}
                        </span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`chip${row.mode === 'allow' ? ' gold' : ''}`}
                        style={row.mode === 'deny' ? { color: 'var(--color-danger, #c97a7a)' } : undefined}
                      >
                        {modeLabel[row.mode]}
                      </span>
                    </td>
                    <td className="dim" style={{ fontSize: 11.5 }}>{row.note || '—'}</td>
                    <td className="dim" style={{ fontSize: 11 }}>{thaiDateTime(row.created_at)}</td>
                    <td>
                      <span className={`chip${row.enabled ? ' gold' : ''}`}>
                        {row.enabled ? 'เปิดใช้' : 'ปิด'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {confirmId === row.id ? (
                        <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            className="btn btn-sm"
                            disabled={remove.isPending}
                            style={{ color: 'var(--color-danger, #c97a7a)' }}
                            onClick={() => remove.mutate(row.id)}
                          >
                            {remove.isPending ? 'กำลังลบ…' : 'ยืนยัน'}
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setConfirmId(null)}>ยกเลิก</button>
                        </span>
                      ) : (
                        <button className="btn btn-ghost btn-sm" onClick={() => setConfirmId(row.id)}>ลบกฎ</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {remove.isError ? (
          <div className="form-error" style={{ marginTop: 10 }}>
            {remove.error instanceof Error ? remove.error.message : 'ลบกฎไม่สำเร็จ'}
          </div>
        ) : null}
      </div>

      <div className="card card-pad" style={{ marginTop: 22 }}>
        <div className="card-label">ลำดับการตัดสินใจ</div>
        <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
          <li>กฎที่ปิดใช้จะถูกข้ามทั้งหมด · ถ้าไม่มีกฎเปิดใช้ ระบบอนุญาตทุกคำขอ</li>
          <li>กฎปฏิเสธถูกตรวจก่อนและชนะเสมอ แม้จะมีกฎอนุญาตที่ตรงกับคำขอเดียวกัน</li>
          <li>เมื่อมีกฎอนุญาตอย่างน้อยหนึ่งข้อ ระบบเปลี่ยนเป็นปฏิเสธโดยค่าเริ่มต้นสำหรับคำขอที่ไม่ตรงกฎใด</li>
          <li>ภายในกฎหนึ่งข้อ ช่วง IP และรหัสประเทศเป็นเงื่อนไขแบบ หรือ: ตรงอย่างใดอย่างหนึ่งถือว่าตรงกฎ</li>
          <li>ที่อยู่ IP ที่อ่านไม่ได้จะถูกปฏิเสธเมื่อมีกฎเปิดใช้อยู่ เพื่อไม่ให้ header ที่ผิดรูปกลายเป็นช่องทางข้ามกฎ</li>
          <li>ทุกการเพิ่มและลบกฎถูกบันทึกในร่องรอยการตรวจสอบขององค์กร</li>
        </ul>
      </div>
    </>
  )
}
